/**
 * Export a tech pack JSON document to a multi-page A4 PDF via jsPDF.
 */
export async function exportTechPackPdf(techPack, options = {}) {
  const { fileName, imageUrl } = options;
  const { jsPDF } = await import('jspdf');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = margin;

  const cover = techPack?.cover || {};
  const safeName = String(cover.styleName || 'tech-pack')
    .replace(/[^\w\-]+/g, '_')
    .slice(0, 60);
  const outName = fileName || `${safeName}_tech_pack.pdf`;

  const ensureSpace = (needed = 10) => {
    if (y + needed > pageH - margin) {
      addFooter();
      doc.addPage();
      y = margin;
      return true;
    }
    return false;
  };

  const addFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `FashDeck Tech Pack  ·  ${cover.styleCode || cover.styleName || ''}  ·  Page ${pageCount}`,
      margin,
      pageH - 8
    );
  };

  const heading = (text, size = 14) => {
    ensureSpace(12);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(size);
    doc.setTextColor(30);
    doc.text(String(text || ''), margin, y);
    y += size * 0.55 + 3;
  };

  const body = (text, opts = {}) => {
    const { bold = false, color = 50, size = 9.5, gap = 1.5 } = opts;
    const lines = doc.splitTextToSize(String(text || ''), contentW);
    ensureSpace(lines.length * (size * 0.4) + 4);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(color);
    doc.text(lines, margin, y);
    y += lines.length * (size * 0.4) + gap;
  };

  const kv = (label, value) => {
    if (value == null || value === '') return;
    body(`${label}: ${value}`, { size: 9.5 });
  };

  const sectionRule = () => {
    ensureSpace(6);
    doc.setDrawColor(210);
    doc.line(margin, y, pageW - margin, y);
    y += 5;
  };

  const drawTable = (headers, rows) => {
    const colCount = headers.length;
    const colW = contentW / colCount;
    const rowH = 7;
    const drawHeader = () => {
      ensureSpace(rowH + 2);
      doc.setFillColor(245, 243, 255);
      doc.rect(margin, y - 4, contentW, rowH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(40);
      headers.forEach((h, i) => {
        doc.text(String(h), margin + i * colW + 1.5, y);
      });
      y += rowH;
    };

    drawHeader();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(50);

    rows.forEach((row) => {
      if (y + rowH > pageH - margin) {
        addFooter();
        doc.addPage();
        y = margin;
        drawHeader();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(50);
      }
      row.forEach((cell, i) => {
        const cellLines = doc.splitTextToSize(String(cell ?? ''), colW - 2);
        doc.text(cellLines[0] || '', margin + i * colW + 1.5, y);
      });
      y += rowH;
    });
    y += 3;
  };

  // Cover
  heading('TECH PACK', 18);
  body(cover.styleName || 'Untitled Style', { bold: true, size: 13, color: 20 });
  kv('Style code', cover.styleCode);
  kv('Season', cover.season);
  kv('Category', cover.productCategory);
  kv('Product type', cover.productType);
  kv('Fit', cover.fit);
  kv('Audience', cover.targetAudience);
  kv('Confidence', cover.confidence);
  if (cover.description) {
    y += 1;
    body(cover.description, { size: 9 });
  }

  const fetchImageDataUrl = async (url) => {
    const res = await fetch(url, { mode: 'cors', cache: 'force-cache' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  if (imageUrl) {
    try {
      ensureSpace(70);
      const dataUrl = await fetchImageDataUrl(imageUrl);
      if (dataUrl) {
        const imgW = 55;
        const imgH = 70;
        doc.addImage(dataUrl, 'JPEG', pageW - margin - imgW, margin + 8, imgW, imgH);
      }
    } catch {
      // Image optional for PDF; ignore CORS / load failures
    }
  }

  sectionRule();

  // Technical flat sketches (front/back, dimensioned)
  const flatSketches = Array.isArray(techPack?.flatSketches) ? techPack.flatSketches : [];
  if (flatSketches.length) {
    addFooter();
    doc.addPage();
    y = margin;
    heading('Technical Flat Sketches');
    body(
      `Dimensioned ${flatSketches.map((s) => s.view).join(' & ')} flats — base size ${techPack?.measurements?.baseSize || 'M'}, measurements in cm.`,
      { size: 9, color: 90 }
    );

    const sketchW = (contentW - 8) / 2;
    const sketchH = sketchW * (4 / 3);
    let x = margin;
    const rowY = y + 2;
    for (const sketch of flatSketches.slice(0, 2)) {
      try {
        const dataUrl = await fetchImageDataUrl(sketch.imageUrl);
        if (dataUrl) {
          doc.setDrawColor(210);
          doc.rect(x, rowY, sketchW, sketchH);
          doc.addImage(dataUrl, 'JPEG', x + 1, rowY + 1, sketchW - 2, sketchH - 2);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(90);
          doc.text(
            sketch.label || `${sketch.view === 'front' ? 'Front' : 'Back'} flat`,
            x,
            rowY + sketchH + 4.5
          );
        }
      } catch {
        // Skip a sketch that fails to load; the rest of the PDF is unaffected
      }
      x += sketchW + 8;
    }
    y = rowY + sketchH + 10;
    sectionRule();
  }

  // Design overview
  const overview = techPack?.designOverview || {};
  heading('Design Overview');
  kv('Silhouette', overview.silhouette);
  kv('Neckline', overview.neckline);
  kv('Sleeves', overview.sleeves);
  kv('Length', overview.garmentLength);
  if (overview.closures?.length) body(`Closures: ${overview.closures.join(', ')}`);
  if (overview.keyDetails?.length) body(`Key details: ${overview.keyDetails.join('; ')}`);
  sectionRule();

  // Materials
  const materials = techPack?.materials || {};
  heading('Materials');
  if (materials.shell?.name) {
    body(
      `Shell: ${materials.shell.name}` +
        (materials.shell.composition ? ` — ${materials.shell.composition}` : '') +
        (materials.shell.weightGsm != null ? ` (${materials.shell.weightGsm} GSM)` : '')
    );
    if (materials.shell.notes) body(materials.shell.notes, { size: 8.5, color: 90 });
  }
  if (materials.lining?.name) {
    body(`Lining: ${materials.lining.name}${materials.lining.composition ? ` — ${materials.lining.composition}` : ''}`);
  }
  if (materials.interlining?.name) body(`Interlining: ${materials.interlining.name}`);
  if (materials.trims?.length) body(`Trims: ${materials.trims.join(', ')}`);
  sectionRule();

  // BOM
  heading('Bill of Materials');
  const bom = Array.isArray(techPack?.billOfMaterials) ? techPack.billOfMaterials : [];
  if (bom.length) {
    drawTable(
      ['Item', 'Spec', 'Color', 'Qty', 'Unit', 'Placement'],
      bom.map((r) => [r.item, r.specification, r.colorRef, r.quantity, r.unit, r.placement])
    );
  } else {
    body('No BOM rows.');
  }
  sectionRule();

  // Colorways
  heading('Colorways');
  const colorways = Array.isArray(techPack?.colorways) ? techPack.colorways : [];
  if (colorways.length) {
    colorways.forEach((c) => {
      body(`${c.isPrimary ? '★ ' : ''}${c.name}${c.hex ? ` (${c.hex})` : ''}${c.description ? ` — ${c.description}` : ''}`);
    });
  } else {
    body('No colorways listed.');
  }
  sectionRule();

  // Construction
  const construction = techPack?.construction || {};
  heading('Construction');
  kv('Seam allowance', construction.seamAllowanceCm != null ? `${construction.seamAllowanceCm} cm` : null);
  if (construction.stitchTypes?.length) body(`Stitch types: ${construction.stitchTypes.join(', ')}`);
  if (construction.finishing?.length) body(`Finishing: ${construction.finishing.join('; ')}`);
  if (construction.qualityChecks?.length) body(`QC: ${construction.qualityChecks.join('; ')}`);
  sectionRule();

  // Artwork
  const artwork = Array.isArray(techPack?.artworkPlacements) ? techPack.artworkPlacements : [];
  heading('Artwork / Embellishment Placements');
  if (artwork.length) {
    artwork.forEach((a) => {
      body(`${a.type || 'detail'} @ ${a.placement}${a.sizeCm ? ` (${a.sizeCm})` : ''}${a.notes ? ` — ${a.notes}` : ''}`);
    });
  } else {
    body('None specified.');
  }
  sectionRule();

  // Labels
  const labels = techPack?.labelsAndPackaging || {};
  heading('Labels & Packaging');
  kv('Brand label', labels.brandLabel);
  kv('Care label', labels.careLabel);
  kv('Size label', labels.sizeLabel);
  kv('Hangtag', labels.hangtag);
  kv('Packing', labels.packing);
  sectionRule();

  // Measurements
  const measurements = techPack?.measurements || {};
  heading('Graded Measurements');
  body(`Unit: ${measurements.unit || 'cm'}  ·  Base size: ${measurements.baseSize || '—'}`, { size: 9 });
  const sizes = Array.isArray(measurements.sizes) ? measurements.sizes : [];
  const poms = Array.isArray(measurements.pointsOfMeasure) ? measurements.pointsOfMeasure : [];
  if (poms.length && sizes.length) {
    drawTable(
      ['POM', ...sizes],
      poms.map((p) => [p.pom || p.code, ...sizes.map((s) => p.values?.[s] ?? '—')])
    );
  } else {
    body('No measurement chart generated.');
  }

  const ease = techPack?.easeAndGradingNotes || {};
  if (ease.easeApplied || ease.gradeLogic) {
    sectionRule();
    heading('Ease & Grading Notes');
    kv('Ease type', ease.easeType);
    if (ease.easeApplied) body(ease.easeApplied);
    if (ease.gradeLogic) body(ease.gradeLogic);
    kv('Reference', ease.referenceUsed);
  }

  const notes = Array.isArray(techPack?.factoryNotes) ? techPack.factoryNotes : [];
  if (notes.length) {
    sectionRule();
    heading('Factory Notes');
    notes.forEach((n, i) => body(`${i + 1}. ${n}`));
  }

  addFooter();
  doc.save(outName);
  return outName;
}
